import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { read } from "xlsx";
import dayjs from "dayjs";
import {
  Radio,
  Checkbox,
  Tooltip,
  Stack,
  FormLabel,
  Input,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Button,
  Card,
  RadioGroup,
  FormControlLabel,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListItemText,
  Box,
  Typography,
  Chip,
  Skeleton,
  Popover,
  IconButton as MuiIconButton,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
} from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import format from "date-fns/format";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import ViewIcon from "@mui/icons-material/Visibility";
import { toast } from "react-toastify";
import { CommonButton } from "../common/commonButton";
import { useStyles } from "./paymentUpdatesContainerStyles";
import { CommonPaginationContainer, CommonFilterSidebar } from "../common";
import { } from "../../utils/generalFunctions";
import instance from "../../redux/api";
import { premiumBDX, exceptionReport, premiumBDXFile, premiumBDXReadData, premiumBDXDemoSummary } from "../../Services/api";
import DialogBox from "../common/dialog";
import CustomDateRangePicker from "../common/CustomDateRangePicker";
import GetViewTable from "./GetViewTable";
import DemoWorkflowNav from "./DemoWorkflowNav";
import { getPaginationCount } from "../CashAllocation/utils";
import { CustomLoader } from "../CustomLoader";
import { Accordion, AccordionSummary, AccordionDetails, useMediaQuery, useTheme } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PendingIcon from '@mui/icons-material/Pending';
import CancelIcon from '@mui/icons-material/Cancel';
import SpeedIcon from '@mui/icons-material/Speed';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';
import { commonToast } from "../common";
import "../common/commonPageStyles.css";
import {
  primaryButtonStyle,
  primaryButtonHoverColor,
} from "../common/commonButtonStyles";

ChartJS.register(ArcElement, ChartTooltip, Legend);

function handleAmount(val) {
  const options = {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return (
    (Number(val) && Number(val).toLocaleString("en-US", options)) ||
    (val === 0 && parseInt(val).toFixed(2)) ||
    val
  );
}

// DEMO: status column
const DEMO_STATUS_COLORS = {
  "Matched": { color: "#4caf50", bg: "rgba(76, 175, 80, 0.1)" },
  "Needs Review": { color: "#ff9800", bg: "rgba(255, 152, 0, 0.1)" },
  "Ready for Approval": { color: "#9c27b0", bg: "rgba(156, 39, 176, 0.1)" },
};

const getDemoStatusStyle = (status) =>
  DEMO_STATUS_COLORS[status] ?? { color: "#1672DE", bg: "rgba(22, 114, 222, 0.1)" };

// DEMO: money polish — $ prefix, muted zero, red negatives
const renderPremiumMoneyCell = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) {
    return <span style={{ color: "#9ca3af", fontWeight: 500 }}>—</span>;
  }
  const color = num < 0 ? "#f44336" : "#374151";
  return (
    <span style={{ color, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
      {num < 0 ? "-" : ""}${handleAmount(Math.abs(num))}
    </span>
  );
};

// DEMO: self-explanatory processing status pill with a click-to-open stepper
const ProcessingPipelineCell = ({ row, animatedDoneCount = null }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const baseSteps = [
    { label: "File uploaded", desc: "BDX workbook received and stored", done: !!row?.upload_status },
    { label: "Sheet parsed", desc: "Columns validated and mapped", done: !!row?.upload_status },
    { label: "Certificates created", desc: "Premium records written to the ledger", done: !!row?.is_prembdx_generated },
    { label: "Reconciled", desc: "Matched against cash allocations", done: !!row?.is_exception_generated },
    {
      label: "Ready for payment",
      desc: "Payout summary available to release",
      done: !!row?.is_exception_generated && Number(row?.total_rows) > 0,
    },
  ];

  const errorText = String(row?.error_message ?? "").trim();
  const failed = Boolean(errorText);
  const firstIncompleteIndex = baseSteps.findIndex((step) => !step.done);
  let failedStepIndex = firstIncompleteIndex >= 0 ? firstIncompleteIndex : baseSteps.length - 1;

  // The database has three processing flags rather than one flag per display step.
  // Use the recorded error to identify the failed stage more precisely when possible.
  if (/payout|payment summary/i.test(errorText)) failedStepIndex = 4;
  else if (/exception|reconcil/i.test(errorText)) failedStepIndex = 3;
  else if (/saving bdx|bdx data|certificate/i.test(errorText)) failedStepIndex = 2;
  else if (/parse|sheet|column/i.test(errorText)) failedStepIndex = 1;
  else if (/upload|s3|file/i.test(errorText)) failedStepIndex = 0;

  const realDoneCount = baseSteps.filter((step) => step.done).length;
  const doneCount =
    animatedDoneCount == null || failed
      ? realDoneCount
      : Math.min(animatedDoneCount, realDoneCount);
  const steps = baseSteps.map((step, index) => ({
    ...step,
    done: failed ? index < failedStepIndex && step.done : index < doneCount && step.done,
    failed: failed && index === failedStepIndex,
  }));
  const complete = !failed && doneCount === steps.length;
  const color = failed ? "#c62828" : complete ? "#2e7d32" : doneCount > 0 ? "#ed6c02" : "#9e9e9e";
  const bg = failed ? "#ffebee" : complete ? "#e8f5e9" : doneCount > 0 ? "#fff4e5" : "#f3f4f6";

  return (
    <>
      <Box
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "6px",
          px: "10px",
          py: "4px",
          borderRadius: "14px",
          backgroundColor: bg,
          border: `1px solid ${color}33`,
          cursor: "pointer",
          transition: "all .2s ease",
          "&:hover": {
            borderColor: color,
            boxShadow: `0 0 0 3px ${color}1a`,
          },
        }}
      >
        {failed ? (
          <CancelIcon sx={{ fontSize: 16, color }} />
        ) : complete ? (
          <CheckCircleIcon sx={{ fontSize: 16, color }} />
        ) : (
          <AutorenewIcon
            sx={{
              fontSize: 16,
              color,
              animation: "pipelineSpin 1s linear infinite",
              "@keyframes pipelineSpin": {
                to: { transform: "rotate(360deg)" },
              },
            }}
          />
        )}
        <span style={{ fontSize: 12, fontWeight: 600, color }}>
          {failed ? "Failed" : complete ? "Processed" : "In progress"}
        </span>
        <span style={{ fontSize: 11, fontWeight: 600, color, opacity: 0.75 }}>
          {failed ? `${failedStepIndex + 1}/${steps.length}` : `${doneCount}/${steps.length}`}
        </span>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{ sx: { p: 2, width: 300, borderRadius: "10px" } }}
      >
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12, color: "#111827" }}>
          Processing pipeline
        </div>
        {steps.map((step, i) => (
          <Box
            key={step.label}
            sx={{
              display: "flex",
              gap: "10px",
              position: "relative",
              pb: i < steps.length - 1 ? 2 : 0,
            }}
          >
            {i < steps.length - 1 && (
              <Box
                sx={{
                  position: "absolute",
                  left: 9,
                  top: 20,
                  bottom: 4,
                  width: 2,
                  backgroundColor: step.failed
                    ? "#ef9a9a"
                    : steps[i + 1].done
                      ? "#4caf50"
                      : "#e5e7eb",
                }}
              />
            )}
            {step.failed ? (
              <CancelIcon sx={{ fontSize: 20, color: "#c62828", zIndex: 1, backgroundColor: "#fff" }} />
            ) : step.done ? (
              <CheckCircleIcon sx={{ fontSize: 20, color: "#4caf50", zIndex: 1, backgroundColor: "#fff" }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ fontSize: 20, color: "#d1d5db", zIndex: 1, backgroundColor: "#fff" }} />
            )}
            <div>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: step.failed ? "#c62828" : step.done ? "#1f2937" : "#9ca3af",
                }}
              >
                {step.label}
              </div>
              <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{step.desc}</div>
              {step.failed && (
                <div style={{ fontSize: 11, color: "#c62828", marginTop: 4, lineHeight: 1.35 }}>
                  {errorText}
                </div>
              )}
            </div>
          </Box>
        ))}
      </Popover>
    </>
  );
};

export const inputStyleObj = {
  border: "2px solid #ccc",
  padding: 2,
  paddingLeft: 16,
  fontSize: 13,
  height: 38,
  width: "260px",
  borderRadius: 5,
};
export const selectStyleObj = {
  border: "1px solid #ccc",
  padding: 2,
  paddingLeft: 16,
  fontSize: 13,
  width: "100%",
  height: 38,
  borderRadius: 5,
};
export const filterInputStyleObj = {
  border: "2px solid #ccc",
  padding: 2,
  paddingLeft: 16,
  fontSize: 13,
  height: 38,
  width: "100%",
  borderRadius: 5,
};

export const buttonsFixations = {
  bottom: 15,
  left: 5,
  right: 0,
  backgroundColor: "white",
  borderTop: "1px solid #ddd",
  padding: "10px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "flex-end",
  gap: "10px",
  width: "98%",
  position: "absolute",
}

// export const errorSpanStyle = {
//   color: 'red',
//   fontSize: '14px',
//   fontWeight: 600,
//   marginTop: "1.5%",  
// }

const CustomTooltip = styled(Tooltip)({
  '& .MuiTooltip-tooltip': {
    backgroundColor: 'red !important',
  },
});

// Month mapping for display labels
const monthLabelMap = {
  "jan": "January",
  "feb": "February",
  "mar": "March",
  "apr": "April",
  "may": "May",
  "jun": "June",
  "jul": "July",
  "aug": "August",
  "sep": "September",
  "oct": "October",
  "nov": "November",
  "dec": "December",
};

const dialogStyle = {
  maxWidth: "95vw",
  width: "85vw",
  margin: "auto",
  maxHeight: "80vh",
};

const PremiumBDX = () => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const policyNo = new URLSearchParams(search).get("policyNo");
  const fileName = new URLSearchParams(search).get("fileName");

  const { userData } = useSelector((state) => state?.user);

  const [selectedValue, setSelectedValue] = useState({});
  const [searchFieldsParams, setSearchFieldsParams] = useState({
    filename: [],
    month: [],
    year: [],
    lob: [],
  });
  
  // State for available filter options
  const [availableFileNames, setAvailableFileNames] = useState([]);
  const [availableLOBs, setAvailableLOBs] = useState([]);

  const [paginationCount, setPaginationCount] = useState(0);
  const [currentRow, setCurrentRow] = useState("");
  const [txnMode, setTxnMode] = useState("");

  const [isOpenAddFileDialog, setOpenAddFileDialog] = useState(false);
  const [isOpenViewDialog, setOpenViewDialog] = useState(false);

  const toggle = useSelector((state) => state.toggleSideMenu);
  const [fromDateValue, setFromDateValue] = useState("");
  const [toDateValue, setToDateValue] = useState("");

  const [isOpenWorkflowDialog, setIsOpenWorkflowDialog] = useState(false);

  const [filenameArray, setFilenameArray] = useState([]);
  const [certificateRefArray, setCertificateRefArray] = useState([]);

  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [isClearPerformed, setIsClearPerformed] = useState(false);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [yearMonthData, setYearMonthData] = useState([]);

  // DEMO: summary cards
  const [demoSummary, setDemoSummary] = useState(null);
  const [isDemoSummaryLoading, setIsDemoSummaryLoading] = useState(true);
  const [lastUploadStats, setLastUploadStats] = useState(null);
  const [pipelineReveal, setPipelineReveal] = useState(null);

  const refreshDemoSummary = useCallback(async () => {
    try {
      const response = await instance.get(premiumBDXDemoSummary);
      if (response?.status === 200) {
        setDemoSummary(response.data ?? null);
      }
    } catch (error) {
      console.log("demo summary error", error);
    } finally {
      setIsDemoSummaryLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshDemoSummary();
  }, [refreshDemoSummary]);

  // DEMO: reveal the real completed workflow slowly enough for a person to see it.
  // This affects only the newest successful upload; stored processing flags remain unchanged.
  useEffect(() => {
    if (!pipelineReveal || pipelineReveal.visibleCount >= 5) return undefined;

    const timer = window.setTimeout(() => {
      setPipelineReveal((current) =>
        current
          ? { ...current, visibleCount: Math.min(current.visibleCount + 1, 5) }
          : current,
      );
    }, 850);

    return () => window.clearTimeout(timer);
  }, [pipelineReveal]);

  // DEMO: once the reveal completes, keep the row highlighted briefly, then clear.
  useEffect(() => {
    if (!pipelineReveal || pipelineReveal.visibleCount < 5) return undefined;
    const timer = window.setTimeout(() => setPipelineReveal(null), 2500);
    return () => window.clearTimeout(timer);
  }, [pipelineReveal]);

  const matchedCount = demoSummary?.matched_count ?? 0;
  const needsReview = demoSummary?.needs_review_count ?? 0;
  const matchedTotal = matchedCount + needsReview;
  const matchRatePct =
    matchedTotal > 0 ? Math.round((matchedCount / matchedTotal) * 100) : 0;

  // DEMO: matched vs needs-review donut
  const matchChartData = {
    labels: ["Matched", "Needs Review"],
    datasets: [
      {
        data: [matchedCount, needsReview],
        backgroundColor: ["#4caf50", "#ff9800"],
        borderWidth: 0,
        hoverOffset: 6,
      },
    ],
  };

  const matchChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "72%",
    layout: { padding: 6 },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => ` ${ctx.label}: ${ctx.parsed}`,
        },
      },
    },
  };

  // DEMO: draw the match-rate % at the true centre of the ring (no manual offsets)
  const matchCenterTextPlugin = {
    id: "matchCenterText",
    afterDatasetsDraw(chart) {
      const meta = chart.getDatasetMeta(0);
      const arc = meta?.data?.[0];
      if (!arc) return;
      const { ctx } = chart;
      const { x, y } = arc;
      ctx.save();
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#1672DE";
      ctx.font = "700 20px Avenir, Arial, sans-serif";
      ctx.fillText(`${matchRatePct}%`, x, y - 6);
      ctx.fillStyle = "#9ca3af";
      ctx.font = "600 10px Avenir, Arial, sans-serif";
      ctx.fillText("MATCHED", x, y + 12);
      ctx.restore();
    },
  };

  // DEMO: summary cards
  const totalCerts = demoSummary?.total_records ?? 0;
  const needsReviewCount = demoSummary?.needs_review_count ?? 0;
  const demoSummaryCards = [
    {
      label: "Total Files",
      value: demoSummary?.total_files ?? "—",
      sub: `${totalCerts} certificate${totalCerts === 1 ? "" : "s"}`,
      color: "#1672DE",
      Icon: InsertDriveFileOutlinedIcon,
    },
    {
      label: "Total Premium",
      value: demoSummary?.total_premium != null ? `$${handleAmount(demoSummary.total_premium)}` : "—",
      sub: "settlement currency",
      color: "#1672DE",
      Icon: PaidOutlinedIcon,
    },
    {
      label: "Matched",
      value: demoSummary?.matched_count ?? "—",
      sub: `${matchRatePct}% of exceptions`,
      color: "#4caf50",
      Icon: TaskAltIcon,
    },
    {
      label: "Needs Review",
      value: needsReviewCount,
      sub: needsReviewCount > 0 ? "requires action" : "all clear",
      color: "#ff9800",
      Icon: ReportProblemOutlinedIcon,
      highlight: needsReviewCount > 0,
    },
  ];

  const handleMainCheckboxChange = (event) => {
    const { value } = event.target;
    const [filename, sheetname] = value.split('|');
    
    // Create a unique key based on filename and sheetname
    const key = `${filename}|${sheetname}`;

    // Toggle the selection
    setSelectedValue((prev) => ({
      ...prev,
      [key]: !prev[key], // Toggle the checked status
    }));
  };

  const currentSkipNumber = () => {
    return pageState?.page === 1
      ? 0
      : pageState?.page === 0
        ? pageState?.page
        : pageState?.page - 1;
  };

  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  const [filenameToReplace, setFilenameToReplace] = useState(null);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [excelFileSheetNames, setExcelFileSheetNames] = useState(null);
  const [selectedExcelSheetName, setSelectedExcelSheetName] = useState(null);
  const [selectedExcelSheetJson, setSelectedExcelSheetJson] = useState(null);
  const [currentTotalFNP, setCurrentTotalFNP] = useState(0);
  const [currentSelectedSheetRowCount, setCurrentSelectedSheetRowCount] = useState(0);
  const [sheetTabName, setSheetTabName] = useState("");
  const [bdxfileName, setBdxFileName] = useState("");
  const [commission, setCommission] = useState(0);
  const [open, setOpen] = useState(false);
  const [sheetNameToReplace, setSheetNameToReplace] = useState(null);
  const [isApiProgressBar, setIsApiProgressBar] = useState(false);
  const [isModelProgressBar, setIsModelProgressBar] = useState(false);

  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);

  //styles
  const classes = useStyles();

  const Container = {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    gap: 0,
    height: "calc(100vh - 180px)",
    maxHeight: "calc(100vh - 180px)",
    overflow: "hidden",
    backgroundColor: "#fafafa",
  };

  const handleRadioChange = (e) => {
    const isChecked = e.target.checked;
    setSelectedValue(isChecked ? e.target.value : "");
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (!value) {
      handleClearSearchList();
    }
    console.log("name", name, "value", value);
    setSearchFieldsParams((v) => ({
      ...v,
      [name]: value,
    }));
  };

  // Build query params for a single filter combination
  const buildQueryParams = (singleFilename, singleMonth, singleYear, singleLob) => {
    let queryParams = "";

    if (singleFilename) {
      queryParams = `${queryParams}&file_name=${encodeURIComponent(singleFilename)}`;
    }

    if (singleMonth) {
      const monthValue = singleMonth.substring(0, 3).toLowerCase();
      queryParams = `${queryParams}&month=${encodeURIComponent(monthValue)}`;
    }

    if (singleYear) {
      queryParams = `${queryParams}&year=${encodeURIComponent(singleYear)}`;
    }

    if (singleLob) {
      queryParams = `${queryParams}&lob=${encodeURIComponent(singleLob)}`;
    }

    if (fromDateValue && toDateValue) {
      let formattedFromDateValue = format(fromDateValue, "yyyy-MM-dd");
      let formattedToDateValue = format(toDateValue, "yyyy-MM-dd");
      queryParams = `${queryParams}&from_date=${formattedFromDateValue}&to_date=${formattedToDateValue}`;
    }

    return queryParams;
  };

  const handleSearchPaymentUpdatesList = async () => {
    try {
      setPageState((old) => ({ ...old, isLoading: true }));
      setIsSearchPerformed(true);
      setIsClearPerformed(false);

      const { filename, month, year, lob } = searchFieldsParams;
      
      // Convert to arrays if they're not already
      const filenames = Array.isArray(filename) ? filename : (filename ? [filename] : []);
      const months = Array.isArray(month) ? month : (month ? [month] : []);
      const years = Array.isArray(year) ? year : (year ? [year] : []);
      const lobs = Array.isArray(lob) ? lob : (lob ? [lob] : []);

      console.log("=== handleSearchPaymentUpdatesList ===");
      console.log("Filter values:", { filenames, months, years, lobs });

      // If no filters selected, use normal search
      if (filenames.length === 0 && months.length === 0 && years.length === 0 && lobs.length === 0) {
        let queryParams = `?skip=0&pageSize=${pageState?.pageSize}`;
        if (fromDateValue && toDateValue) {
          let formattedFromDateValue = format(fromDateValue, "yyyy-MM-dd");
          let formattedToDateValue = format(toDateValue, "yyyy-MM-dd");
          queryParams = `${queryParams}&from_date=${formattedFromDateValue}&to_date=${formattedToDateValue}`;
        }
        
        const response = await instance.get(`${premiumBDXFile}${queryParams}`);
        if (response.status === 200) {
          setPageState((old) => ({
            ...old,
            isLoading: false,
            total: response?.data?.count,
            data: response?.data?.data ?? [],
            page: 1,
          }));
          setPaginationCount(getPaginationCount(response?.data?.count || 0, pageState?.pageSize));
        }
        return;
      }

      // Generate all combinations of filter values
      // Use empty array to represent "no filter" for that field
      const filenameCombos = filenames.length > 0 ? filenames : [null];
      const monthCombos = months.length > 0 ? months : [null];
      const yearCombos = years.length > 0 ? years : [null];
      const lobCombos = lobs.length > 0 ? lobs : [null];

      // Generate all combinations
      const allCombinations = [];
      filenameCombos.forEach(f => {
        monthCombos.forEach(m => {
          yearCombos.forEach(y => {
            lobCombos.forEach(l => {
              allCombinations.push({ filename: f, month: m, year: y, lob: l });
            });
          });
        });
      });

      console.log("Total combinations to query:", allCombinations.length);

      // Make API calls for each combination
      const allResults = [];
      const seenIds = new Set();
      let maxCount = 0;

      for (const combo of allCombinations) {
        const queryParams = buildQueryParams(combo.filename, combo.month, combo.year, combo.lob);
        const fullQuery = `?skip=0&pageSize=${pageState?.pageSize}${queryParams}`;
        
        try {
          console.log("Querying with:", fullQuery);
          const response = await instance.get(`${premiumBDXFile}${fullQuery}`);
          
          if (response.status === 200 && response.data?.data) {
            // Add results that haven't been seen yet
            response.data.data.forEach(item => {
              if (!seenIds.has(item.id)) {
                seenIds.add(item.id);
                allResults.push(item);
              }
            });
            maxCount = Math.max(maxCount, response.data.count || 0);
          }
        } catch (error) {
          console.error("Error in combination query:", error);
        }
      }

      console.log("Total unique results:", allResults.length);
      console.log("Max count from API:", maxCount);

      setPageState((old) => ({
        ...old,
        isLoading: false,
        total: allResults.length, // Use actual unique count
        data: allResults,
        page: 1,
      }));
      setPaginationCount(getPaginationCount(allResults.length, pageState?.pageSize));
    } catch (error) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("=== API ERROR ===");
      console.error("Error details:", error);
      console.error("Error response:", error?.response);
      console.error("Error message:", error?.message);
    }
  };

  const handleClearSearchList = async () => {
    setIsSearchPerformed(false);
    setIsClearPerformed(true);
    setSearchFieldsParams({
      filename: [],
      year: [],
      month: [],
      lob: [],
    });
    setFromDateValue("");
    setToDateValue("");
  };

  const isFilterBtnDisable = () => {
    let disabled = true;
    const { filename, month, year, lob } = searchFieldsParams;
    if (
      (Array.isArray(filename) && filename.length > 0) ||
      (Array.isArray(month) && month.length > 0) ||
      (Array.isArray(year) && year.length > 0) ||
      (Array.isArray(lob) && lob.length > 0) ||
      fromDateValue !== "" ||
      toDateValue !== ""
    ) {
      disabled = false;
    }
    return disabled;
  };

  const loadData = async () => {
    try {
      setPageState((old) => ({
        ...old,
        isLoading: true,
      }));

      let queryParams = ``;

      if (fileName) {
        queryParams = `${queryParams}&file_name=${fileName.replace(
          "&",
          "%26",
        )}`;
      }
      if (policyNo) {
        queryParams = `${queryParams}&Certificate_Ref=${policyNo}`;
      }

      // add search query param if the search is enabled
      // For pagination, use first value of each filter array
      if (isSearchPerformed) {
        const { filename, month, year, lob } = searchFieldsParams;
        const firstFilename = Array.isArray(filename) && filename.length > 0 ? filename[0] : (filename || null);
        const firstMonth = Array.isArray(month) && month.length > 0 ? month[0] : (month || null);
        const firstYear = Array.isArray(year) && year.length > 0 ? year[0] : (year || null);
        const firstLob = Array.isArray(lob) && lob.length > 0 ? lob[0] : (lob || null);
        
        const getSearchQueryParam = buildQueryParams(firstFilename, firstMonth, firstYear, firstLob);
        if (getSearchQueryParam) {
          queryParams = `${queryParams}${getSearchQueryParam}`;
        }
      }

      const response = await instance.get(
        `${premiumBDXFile}?skip=${currentSkipNumber()}&pageSize=${pageState?.pageSize
        }${queryParams}`,
      );

      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));

      if (response.status === 200) {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: response?.data?.data ?? [],
        }));
      } else {
        setPageState((old) => ({
          ...old,
          total: response?.data?.count,
          data: [],
        }));
        setPaginationCount(0);
      }
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      setPaginationCount(0);
      console.error("err", err);
    }
  };

  const downloadFile = async (currentRowData) => {
    let queryParams = `?`;
    let filename = currentRowData?.filename || "";
    let tab_name = currentRowData?.sheettab || "";
    if (filename) {
      queryParams = `${queryParams}&file_name=${filename.replace("&", "%26")}`;
    }
    if (tab_name) {
      queryParams = `${queryParams}&tab_name=${tab_name}`;
    }
    queryParams = `${queryParams}&action=download`;

    const response = await instance.get(`${premiumBDXFile}${queryParams}`);
    console.log("download response", response);
    const file_link = Object.values(response.data.Data)[0];
    console.log("file link", file_link);
    let link = document.createElement("a");
    console.log(link, "link123");
    link.href = file_link;
    link.click();
  };

  const handleActionButton = (currentRowData, currentTxnMode) => {
    setCurrentRow(currentRowData);
    setTxnMode(currentTxnMode);
    switch (currentTxnMode) {
      case "VIEW":
        setOpenViewDialog(true);
        handleGetFileToView(currentRowData);

        break;
      case "EDIT":
        setFilenameToReplace(currentRowData.filename);
        setSheetNameToReplace(currentRowData.sheettab);
        setOpenAddFileDialog(true);
        break;

      case "DELETE":
        console.log(currentRowData, "currentRowData123");
        setFilenameToReplace(currentRowData.filename);
        setOpen(true);
        break;

      case "DOWNLOAD":
        downloadFile(currentRowData);
        break;

      case "WORKFLOW":
        setIsOpenWorkflowDialog(true);
        break;

      default:
        break;
    }
  };

  const handleDeleteConfirmed = async () => {
    try {

      const response = await instance.delete(`${premiumBDX}?id=${currentRow?.id}&action=archieve&analyst_id=${userData.id}`);

      if (response.status === 200) {
        commonToast("success", `${currentRow?.filename} deleted successfully`);
        setOpen(false);
        setFilenameToReplace(null);
        setSheetNameToReplace(null);
        loadData();
      } else {
        commonToast("error", `Error deleting item: ${response.statusText}`);
        setOpen(false);
        setFilenameToReplace(null);
        setSheetNameToReplace(null);
      }
    } catch (error) {
      commonToast("error", `Error deleting item: ${error.message}`);
      setOpen(false);
      setFilenameToReplace(null);
      setSheetNameToReplace(null);
    }
  };

  const handleDeleteCancelled = () => {
    setOpen(false);
    setFilenameToReplace(null);
    setSheetNameToReplace(null);
  };

  const [isFileSelected, setIsFileSelected] = useState(false);
  const [filesData, setFilesData] = useState([]);
  const [fileSheetNamesMap, setFileSheetNamesMap] = useState({});
  const [selectedSheetNames, setSelectedSheetNames] = useState({});
  const [allFilesResponseData, setAllFilesResponseData] = useState([]);
  const allSheetsSelected = filesData.every(file => selectedSheetNames[file.name]);
  const [filesBinaryData, setFilesBinaryData] = useState({});


  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleInputFileChange = useCallback((e) => {
    const { name, files } = e.target;
    const allowedFormats = [
      "application/pdf",
      "text/csv",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    if (name === "file" && files.length > 0) {
      setIsFileSelected(true);
      const filesArray = Array.from(files);
      setFilesData(filesArray);
      for (let index = 0; index < filesArray.length; index++) {
        const file = filesArray[index];
        if (file && allowedFormats.includes(file.type)) {
          const reader = new FileReader();
          reader.onload = function (e) {
            var data = e.target.result;
            var workbookInner = read(data, { type: "binary" });
            var sheet_name_list = workbookInner.SheetNames;
            setFileSheetNamesMap(prevMap => ({
              ...prevMap,
              [file.name]: sheet_name_list
            }));
            // Store the binary data in state
            setFilesBinaryData(prevData => ({
              ...prevData,
              [file.name]: data
            }));
          };
          reader.readAsArrayBuffer(file);
        } else {
          alert("Invalid file format. Please select a PDF, CSV, or XLSX file.");
        }
      }
    }
  });

  const setHandleIntermediateView = async (selectedFile, selectedSheetName) => {
    try {
      setIsApiProgressBar(true);
      const form = new FormData();
      form.append("tab_name", selectedSheetName);
      form.append("file", selectedFile);
      form.append("analyst_id", userData.id);

      const response = await instance.post(`${premiumBDXReadData}`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      if (response.status === 200) {
        setAllFilesResponseData(prevData => ({
          ...prevData,
          [selectedFile?.name]: {
            json: response.data.Response,
            total: response.data.total,
            count: response.data.count
          }
        }));
        setCurrentTotalFNP(response.data.total);
        setCurrentSelectedSheetRowCount(response.data.count);
        setSheetTabName(response.data.tab_name ?? "")
        setCommission(response.data.total_commission ?? 0)
        setSelectedExcelSheetJson(response.data.Response);
        setOpenViewDialog(true);
      } else {
        commonToast("error", `Error in retrieving file records`);
      }
    } catch (error) {
      console.log("error", error);
      commonToast("error", error?.data?.Error ?? `Error in retrieving file records`);
    } finally {
      setIsApiProgressBar(false);
    }
  };

  const handleGetFileToView = async (row) => {
    let queryParams = `?`;

    if (row?.filename) {
      queryParams = `${queryParams}file_name=${row?.filename.replace(
        "&",
        "%26",
      )}`;
    }
    if (row?.sheettab) {
      queryParams = `${queryParams}&tab_name=${row?.sheettab}`;
    }
    queryParams = `${queryParams}&action=view`;
    setIsApiProgressBar(true);
    try {
      const response = await instance.get(`${premiumBDXFile}${queryParams}`);
      setSelectedExcelSheetJson(response?.data?.Response);
      setCurrentTotalFNP(response.data.total);
      setCurrentSelectedSheetRowCount(response.data.count);
      setSheetTabName(response.data.tab_name ?? "")
      setBdxFileName(row?.filename)
      setCommission(response.data.total_commission ?? 0)
      setIsApiProgressBar(false);
    } catch (err) {
      setIsApiProgressBar(false);
      console.log("err", err);
    }
  };

  const handleSubmitFile = async () => {
    try {
      setIsSubmitInProgress(true);

      let payload = {
        action: "add",
        analyst_id: userData.id,
        comments: "automatic workflow initiated",
        initiateWF: true,
        response: selectedExcelSheetJson,
      };
      if (txnMode === "EDIT") {
        payload.action = "archieve";
        payload.old_file_name = filenameToReplace;
      } 

      const responseException = await instance.post(`${exceptionReport}`, payload);
      setIsSubmitInProgress(false);

      if (responseException.status === 200 || responseException.status === 201) {
        if (txnMode === "ADD") {
          commonToast("success", `${responseException.data.Message}`);
        } else {
          commonToast("success", `File ${txnMode === "ADD" ? "added" : "replaced"} successfully`);
        }

        setFileToUpload(null);
        setExcelFileSheetNames(null);
        setSelectedExcelSheetName(null);
        setSelectedExcelSheetJson(null);
        setOpenAddFileDialog(false);
        setFilesData([]);
        setSelectedSheetNames({});
        setOpenViewDialog(false);
        setFilenameToReplace(null);
        setSheetNameToReplace(null);
        setIsSubmitInProgress(false);

        // Navigate Exception Report
        navigate("/BDX/ExceptionReport");
      } else {
        setIsSubmitInProgress(false);
        commonToast("error", `Error ${txnMode === "ADD" ? "adding" : "replacing"} file`);
      }
    } catch (error) {
      console.log("error", error);
      setIsSubmitInProgress(false);
      commonToast("error", `Error ${txnMode === "ADD" ? "adding" : "replacing"} file: ${error.data.Response || ""
        }`);
    }
  };

  const columns = [
    {
      headerName: "",
      headerAlign: "center",
      flex: 0.1,
      type: "actions",
      renderCell: (params) => {
        const filename = params?.row?.filename;
        const sheetname = params?.row?.sheettab;
        const key = `${filename}|${sheetname}`;
        return (
          <>
            <Checkbox
              checked={!!selectedValue[key]}
              onChange={handleMainCheckboxChange}
              value={key}
              name="file-selection"
              style={{ color: "black" }}
            />
          </>
        );
      },
    },
    {
      field: "filename",
      headerName: "File Name",
      headerAlign: "center",
      flex: 1,
      minWidth: 500,
      renderCell: (params) => (
        <>
          <Tooltip title={params.row.filename}>
            <span style={{ textAlign: "left", display: "inline-block", width: "100%", marginLeft: "10px" }}>
              {params.row?.filename.length > 100 
                ? `${params.row.filename.substring(0, 100)}...` 
                : params.row.filename}
            </span>
          </Tooltip>
        </>
      ),
    },
    {
      field: "sheettab",
      headerName: "Sheet Name",
      headerAlign: "center",
      minWidth: 150,
      flex: 1,
    },
    {
      field: "month",
      headerName: "Month",
      headerAlign: "center",
      minWidth: 120,
      flex: 1,
    },
    {
      field: "year",
      headerName: "Year",
      headerAlign: "center",
      minWidth: 100,
      flex: 1,
    },
    {
      field: "lob",
      headerName: "LOB",
      headerAlign: "center",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => <>{params.row?.lob || "-"}</>,
    },
    {
      field: "total",
      headerName: "Final Net Settlement",
      headerAlign: "center",
      minWidth: 170,
      flex: 1,
      cellClassName: "amount-table-cell",
      // DEMO: money polish
      renderCell: (params) => renderPremiumMoneyCell(params.row?.total),
    },
    {
      field: "commission_amount",
      headerName: "Commission Amount",
      headerAlign: "center",
      minWidth: 170,
      flex: 1,
      cellClassName: "amount-table-cell",
      // DEMO: money polish
      renderCell: (params) => renderPremiumMoneyCell(params.row?.commission_amount),
    },
    {
      // DEMO: match rate
      field: "match_rate",
      headerName: "Match Rate",
      flex: 1,
      minWidth: 160,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const matched = Number(params.row?.matched_rows);
        const total = Number(params.row?.total_rows);
        if (!Number.isFinite(matched) || !Number.isFinite(total) || total <= 0) {
          return <>—</>;
        }
        const pct = Math.min(100, Math.round((matched / total) * 100));
        const barColor = pct >= 90 ? "#4caf50" : pct >= 60 ? "#ff9800" : "#f44336";
        return (
          <Tooltip title={`${matched} of ${total} rows matched`}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", width: "100%" }}>
              <Box
                sx={{
                  flex: 1,
                  height: "8px",
                  borderRadius: "4px",
                  backgroundColor: "#eceff4",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: `${pct}%`,
                    height: "100%",
                    backgroundColor: barColor,
                    borderRadius: "4px",
                    transition: "width 0.3s ease",
                  }}
                />
              </Box>
              <span style={{ fontSize: "12px", fontWeight: 600, color: barColor, minWidth: "36px" }}>
                {pct}%
              </span>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      // DEMO: processing pipeline stepper
      field: "processing_pipeline",
      headerName: "Processing",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const isNewestUpload =
          pipelineReveal?.filename === params.row?.filename &&
          (!pipelineReveal?.sheetName || pipelineReveal.sheetName === params.row?.sheettab);
        return (
          <ProcessingPipelineCell
            row={params.row}
            animatedDoneCount={isNewestUpload ? pipelineReveal.visibleCount : null}
          />
        );
      },
    },
    {
      field: "Analyst Name",
      headerName: "Analyst Name",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      renderCell: (params) => <>{params.row["Analyst Name"]}</>,
    },
    {
      field: "processingdate",
      headerName: "Uploaded Date",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      renderCell: (params) => (
        <>
          {dayjs(params.row?.processingdate, "YYYY-MM-DD").format("DD-MM-YYYY")}
        </>
      ),
    },
    {
      field: "error_message",
      headerName: "Error Log",
      flex: 1,
      minWidth: 300,
      headerAlign: "center",
      renderCell: (params) => (
        <>
          <Tooltip title={params.row.error_message || "No error"}>
            <span>
              {params.row.error_message 
                ? params.row.error_message.substring(0, 50) + (params.row.error_message.length > 50 ? "..." : "")
                : (params.row.upload_status ? "Uploaded" : "Uploading...")}
            </span>
          </Tooltip>
        </>
      ),
    },
    {
      field: "columns_not_found",
      headerName: "Columns Not Found",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title={params.row?.columns_not_found || ""}>
          <span>
            {params.row?.columns_not_found
              ? `${params.row.columns_not_found.substring(0, 40)}${params.row.columns_not_found.length > 40 ? "..." : ""}`
              : ""}
          </span>
        </Tooltip>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 160,
      headerAlign: "center",
      renderCell: (params) => {
        // DEMO: status column
        const demoStatus = params.row?.status;
        if (demoStatus) {
          const { color, bg } = getDemoStatusStyle(demoStatus);
          return (
            <Chip
              label={demoStatus}
              sx={{
                backgroundColor: bg,
                color,
                fontWeight: "600",
                fontSize: "12px",
                height: "28px",
                borderRadius: "6px",
                border: `1px solid ${color}`,
              }}
            />
          );
        }
        return (
          <>
            {params.row?.upload_status ? (
              params.row?.is_prembdx_generated && params.row?.is_exception_generated
              ? <CheckCircleIcon style={{ color: 'green' }} />
              : <CancelIcon style={{ color: 'red' }} />
            ) : (
              <PendingIcon style={{ color: 'grey' }} />
            )}
          </>
        );
      },
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      flex: 1,
      minWidth: 100,
      description: "This column has a value getter and is not sortable.",
      cellClassName: "actions",
      getActions: ({ row }) => {
        let actionsList = [];
        actionsList.push(
          <GridActionsCellItem
            icon={
              <Tooltip title="View">
                <ViewIcon />
              </Tooltip>
            }
            label="View"
            className="textPrimary"
            onClick={() => handleActionButton(row, "VIEW")}
            color="inherit"
            disabled={!row.upload_status || !row.is_prembdx_generated}
          />,
        );
        
        actionsList.push(
          <GridActionsCellItem
            icon={
              <Tooltip title="Edit">
                <EditIcon />
              </Tooltip>
            }
            className="textPrimary"
            label="Edit"
            onClick={() => handleActionButton(row, "EDIT")}
            color="inherit"
          />,
        );

        actionsList.push(
          <GridActionsCellItem
            icon={
              <Tooltip title="Delete">
                <DeleteIcon />
              </Tooltip>
            }
            label="delete"
            className="textPrimary"
            onClick={() => handleActionButton(row, "DELETE")}
            color="inherit"
            disabled={!row.upload_status}
          />,
        );

        // actionsList.push(
        //   <GridActionsCellItem
        //     icon={
        //       <Tooltip title="Download">
        //         <DownloadIcon className="action-icon" />
        //       </Tooltip>
        //     }
        //     label="download"
        //     className="textPrimary"
        //     onClick={() => handleActionButton(row, "DOWNLOAD")}
        //     color="inherit"
        //   />,
        // );

        return actionsList;
      },
    },
  ];

  const handleFileSave = async () => {
    // DEMO: upload toast
    let didShowDemoUploadToast = false;
    let lastUploadSummary = null;
    const uploadStartedAt = Date.now();
    try {
      setIsModelProgressBar(true);
      await Promise.all(selectedFiles.map(async (file) => {
        const form = new FormData();
        form.append("tab_name", selectedSheetNames[file?.name]);
        form.append("file", file);
        form.append("analyst_id", userData.id);
        form.append("action", "parse");
        if (txnMode === "EDIT") {
          await instance.delete(`${premiumBDX}?id=${currentRow?.id}&action=archieve&analyst_id=${userData.id}`);
        }
  
        const response = await instance.post(`${premiumBDX}`, form, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // DEMO: upload toast
        const summary = response?.data ?? {};
        lastUploadSummary = {
          ...summary,
          filename: summary.filename ?? file?.name,
          sheetName: selectedSheetNames[file?.name],
        };
        const warnings = Array.isArray(summary.warnings) ? summary.warnings : [];
        didShowDemoUploadToast = true;
        toast.success(
          <div>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{summary.filename ?? file?.name}</div>
            <div style={{ fontSize: 13 }}>Rows processed: {summary.rows_processed ?? "—"}</div>
            <div style={{ fontSize: 13, color: "#2e7d32" }}>Matched: {summary.matched_count ?? "—"}</div>
            <div style={{ fontSize: 13, color: "#b26a00" }}>Needs review: {summary.needs_review_count ?? "—"}</div>
            {warnings.length > 0 && (
              <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12 }}>
                {warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            )}
          </div>,
          { position: toast.POSITION.TOP_RIGHT, autoClose: 8000 },
        );
  
        // if (response.status === 400) {
        //   const errorMessage = response.data?.message || "An error occurred";
        //   console.error("API Error:", errorMessage);
        //   toast.error(errorMessage, {
        //     position: toast.POSITION.TOP_RIGHT,
        //     autoClose: 1000,
        //   });
        // } else if (response.status === 200) {
        //   setAllFilesResponseData(prevData => ({
        //     ...prevData,
        //     [file.name]: { // Ensure to use file.name as the key
        //       json: response.data.Response,
        //       total: response.data.total,
        //       count: response.data.count
        //     }
        //   }));
        //   setSelectedExcelSheetJson(response.data.Response);
        //   setOpenAddFileDialog(false);
        //   loadData();
        // } else {
        //   toast.error(`Error in retrieving file records`, {
        //     position: toast.POSITION.TOP_RIGHT,
        //     autoClose: 1000,
        //   });
        // }
      }));
    } catch (error) {
      console.log("error", error);
      // toast.error(error?.data?.Response ?? `Error in retrieving file records`, {
      //   position: toast.POSITION.TOP_RIGHT,
      //   autoClose: 1000,
      // });
    } finally {
      setIsModelProgressBar(false);
      setSelectedFiles([]);
      setSelectedExcelSheetJson(null);
      setAllFilesResponseData({});
      setOpenAddFileDialog(false);
      setFilesData([]);
      setSelectedSheetNames({});
      loadData();
      refreshDemoSummary();
      if (lastUploadSummary) {
        setLastUploadStats({
          ...lastUploadSummary,
          durationSec: ((Date.now() - uploadStartedAt) / 1000).toFixed(1),
        });
        setPipelineReveal({
          filename: lastUploadSummary.filename,
          sheetName: lastUploadSummary.sheetName,
          visibleCount: 1,
        });
      }
      // DEMO: upload toast
      if (!didShowDemoUploadToast) {
        commonToast("success", "Record added successfully");
      }
    }
  }

  useEffect(() => {
    setPaginationCount(
      getPaginationCount(pageState?.total, pageState?.pageSize),
    );
  }, [pageState?.pageSize, pageState?.total]);

  // Fetch available years, months, LOBs, and file names from existing API data
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Fetch all data without filters to get all available filter options
        const response = await instance.get(`${premiumBDXFile}?skip=0&pageSize=10000`);
        if (response.status === 200 && response.data && response.data.data) {
          const allData = response.data.data;
          
          // Extract unique years and months from the data
          const yearMonthMap = {};
          const allMonthsSet = new Set();
          const fileNamesSet = new Set();
          const lobsSet = new Set();
          
          console.log("=== Fetching Filter Data ===");
          console.log("Total items fetched:", allData.length);
          
          allData.forEach(item => {
            const year = item.year;
            const month = item.month;
            const filename = item.filename;
            const lob = item.lob;
            
            if (year && month) {
              // Group months by year - preserve original case from database
              if (!yearMonthMap[year]) {
                yearMonthMap[year] = new Set();
              }
              // Store month as-is from database (could be lowercase like "nov" or uppercase)
              yearMonthMap[year].add(month);
              allMonthsSet.add(month);
            }
            
            if (filename) {
              fileNamesSet.add(filename);
            }
            
            if (lob) {
              lobsSet.add(lob);
            }
          });
          
          console.log("Year-Month Map:", yearMonthMap);
          console.log("All months set:", Array.from(allMonthsSet));
          console.log("File names set:", Array.from(fileNamesSet));
          console.log("LOBs set:", Array.from(lobsSet));
          
          // Convert to array format for yearMonthData (keep year as string for consistency)
          const yearMonthArray = Object.keys(yearMonthMap).map(year => ({
            year: year.toString(), // Ensure year is string
            months: Array.from(yearMonthMap[year]).sort()
          })).sort((a, b) => parseInt(b.year) - parseInt(a.year));
          
          setYearMonthData(yearMonthArray);
          console.log("Year-Month Array:", yearMonthArray);
          
          // Extract unique years (sorted descending) - keep as strings to match yearMonthData
          const years = Object.keys(yearMonthMap).map(y => y.toString()).sort((a, b) => parseInt(b) - parseInt(a));
          setAvailableYears(years);
          console.log("Available years:", years);
          
          // Extract unique months (sorted)
          const monthsArray = Array.from(allMonthsSet).sort();
          setAvailableMonths(monthsArray);
          console.log("Available months:", monthsArray);
          
          // Extract unique file names (sorted)
          const fileNamesArray = Array.from(fileNamesSet).sort();
          setAvailableFileNames(fileNamesArray);
          console.log("Available file names:", fileNamesArray);
          
          // Extract unique LOBs (sorted)
          const lobsArray = Array.from(lobsSet).sort();
          setAvailableLOBs(lobsArray);
          console.log("Available LOBs:", lobsArray);
        }
      } catch (error) {
        console.error("Error fetching filter data:", error);
      }
    };
    
    fetchFilterData();
  }, []);

  // UseEffect
  useEffect(() => {
    if (isClearPerformed) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClearPerformed]);

  useEffect(() => {
    if (!isClearPerformed) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState?.pageSize, pageState?.page]);

  // State to track loading status for each file
  const [loadingStates, setLoadingStates] = useState(new Map());

  // Function to handle view action
  const handleView = useCallback((file, sheetName) => {
    // Set loading true for the specific file and sheet
    setLoadingStates(prev => new Map(prev).set(`${file.name}-${sheetName}`, true));
    
    setHandleIntermediateView(file, sheetName)
      .finally(() => {
        // Set loading false for the specific file and sheet
        setLoadingStates(prev => {
          const newStates = new Map(prev);
          newStates.set(`${file.name}-${sheetName}`, false);
          return newStates;
        });
      });
  }, [setHandleIntermediateView]);

  // Add a state to track selected files
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Checkbox change handler
  const handleCheckboxChange = (file, isChecked) => {
    setSelectedFiles(prev => {
      if (isChecked) {
        return [...prev, file]; // Add file to the list
      } else {
        return prev.filter(f => f.name !== file.name); // Remove file from the list
      }
    });
  };

  const handleEnter = (e) => {
    if (e.code === "Enter") {
      handleSearchPaymentUpdatesList();
    }
  };

  // Get months available for selected year(s), or all months if no year selected
  const getAvailableMonthsForYear = () => {
    console.log("=== getAvailableMonthsForYear ===");
    console.log("Selected year(s):", searchFieldsParams.year);
    console.log("Available months:", availableMonths);
    console.log("Year-Month data:", yearMonthData);
    
    // If no year selected, return all available months
    if (!searchFieldsParams.year || (Array.isArray(searchFieldsParams.year) && searchFieldsParams.year.length === 0)) {
      const result = availableMonths.length > 0 ? availableMonths : [];
      console.log("No year selected, returning all months:", result);
      return result;
    }
    
    // Handle array of selected years
    const selectedYears = Array.isArray(searchFieldsParams.year) 
      ? searchFieldsParams.year.map(y => y.toString())
      : [searchFieldsParams.year.toString()];
    
    const monthsSet = new Set();
    selectedYears.forEach(selectedYear => {
      const yearData = yearMonthData.find(item => item.year === selectedYear || item.year.toString() === selectedYear);
      if (yearData && yearData.months && yearData.months.length > 0) {
        yearData.months.forEach(month => monthsSet.add(month));
      }
    });
    
    if (monthsSet.size > 0) {
      const result = Array.from(monthsSet).sort();
      console.log("Returning months for selected years:", selectedYears, ":", result);
      return result;
    }
    
    // Fallback: return all months if year data not found
    const result = availableMonths.length > 0 ? availableMonths : [];
    console.log("Year data not found, returning all months:", result);
    return result;
  };

  // Prepare filter configs for CommonFilterSidebar
  const filterConfigs = [
    {
      type: "bankName", // Using bankName type for multi-select checkbox
      label: "Year",
      placeholder: "Search Year",
      items: availableYears,
      selectedItems: Array.isArray(searchFieldsParams.year) ? searchFieldsParams.year.map(y => y.toString()) : [],
      searchValue: "",
      onSearchChange: () => {}, // No search for years
      onItemChange: (yearValue) => {
        const currentYears = Array.isArray(searchFieldsParams.year) ? searchFieldsParams.year : [];
        const yearStr = yearValue.toString();
        if (currentYears.includes(yearStr)) {
          setSearchFieldsParams((v) => ({
            ...v,
            year: currentYears.filter(y => y.toString() !== yearStr),
          }));
        } else {
          setSearchFieldsParams((v) => ({
            ...v,
            year: [...currentYears, yearStr],
          }));
        }
      },
      onClear: () => {
        setSearchFieldsParams((v) => ({
          ...v,
          year: [],
        }));
      },
    },
    {
      type: "bankName", // Using bankName type for multi-select checkbox
      label: "Month",
      placeholder: "Search Month",
      items: getAvailableMonthsForYear().map(monthValue => {
        const monthKey = monthValue.toLowerCase();
        const label = monthLabelMap[monthKey] || monthValue.charAt(0).toUpperCase() + monthValue.slice(1).toLowerCase();
        // Store just the label for display, but we'll map it back to value when selected
        return label;
      }),
      selectedItems: Array.isArray(searchFieldsParams.month) 
        ? searchFieldsParams.month.map(m => {
            const monthKey = m.toLowerCase();
            const label = monthLabelMap[monthKey] || m.charAt(0).toUpperCase() + m.slice(1).toLowerCase();
            return label;
          })
        : [],
      searchValue: "",
      onSearchChange: () => {}, // No search for months
      onItemChange: (labelStr) => {
        // Convert label back to abbreviation
        const reverseMonthMap = {
          "january": "jan", "february": "feb", "march": "mar", "april": "apr",
          "may": "may", "june": "jun", "july": "jul", "august": "aug",
          "september": "sep", "october": "oct", "november": "nov", "december": "dec"
        };
        const monthValue = reverseMonthMap[labelStr.toLowerCase()] || labelStr.substring(0, 3).toLowerCase();
        const currentMonths = Array.isArray(searchFieldsParams.month) ? searchFieldsParams.month : [];
        if (currentMonths.includes(monthValue)) {
          setSearchFieldsParams((v) => ({
            ...v,
            month: currentMonths.filter(m => m !== monthValue),
          }));
        } else {
          setSearchFieldsParams((v) => ({
            ...v,
            month: [...currentMonths, monthValue],
          }));
        }
      },
      onClear: () => {
        setSearchFieldsParams((v) => ({
          ...v,
          month: [],
        }));
      },
    },
    {
      type: "bankName", // Using bankName type for multi-select checkbox
      label: "LOB",
      placeholder: "Search LOB",
      items: availableLOBs,
      selectedItems: Array.isArray(searchFieldsParams.lob) ? searchFieldsParams.lob : [],
      searchValue: "",
      onSearchChange: () => {}, // Can add search functionality later if needed
      onItemChange: (lobValue) => {
        const currentLobs = Array.isArray(searchFieldsParams.lob) ? searchFieldsParams.lob : [];
        if (currentLobs.includes(lobValue)) {
          setSearchFieldsParams((v) => ({
            ...v,
            lob: currentLobs.filter(l => l !== lobValue),
          }));
        } else {
          setSearchFieldsParams((v) => ({
            ...v,
            lob: [...currentLobs, lobValue],
          }));
        }
      },
      onClear: () => {
        setSearchFieldsParams((v) => ({
          ...v,
          lob: [],
        }));
      },
    },
    {
      type: "bankName", // Using bankName type for multi-select checkbox
      label: "File Name",
      placeholder: "Search File Name",
      items: availableFileNames,
      selectedItems: Array.isArray(searchFieldsParams.filename) ? searchFieldsParams.filename : [],
      searchValue: "",
      onSearchChange: () => {}, // Can add search functionality later if needed
      onItemChange: (filenameValue) => {
        const currentFilenames = Array.isArray(searchFieldsParams.filename) ? searchFieldsParams.filename : [];
        if (currentFilenames.includes(filenameValue)) {
          setSearchFieldsParams((v) => ({
            ...v,
            filename: currentFilenames.filter(f => f !== filenameValue),
          }));
        } else {
          setSearchFieldsParams((v) => ({
            ...v,
            filename: [...currentFilenames, filenameValue],
          }));
        }
      },
      onClear: () => {
        setSearchFieldsParams((v) => ({
          ...v,
          filename: [],
        }));
      },
    },
    {
      type: "dateRange",
      label: "Date Range",
      startDate: fromDateValue,
      setStartDate: setFromDateValue,
      endDate: toDateValue,
      setEndDate: setToDateValue,
      maxDate: new Date(),
      onKeyUp: handleEnter,
    },
  ];

  return (
    <div style={Container}>
      {/* Filter Sidebar */}
      <CommonFilterSidebar
        isOpen={filterSidebarOpen}
        filterConfigs={filterConfigs}
        onApplyFilters={handleSearchPaymentUpdatesList}
        onClearFilters={handleClearSearchList}
        isLoading={pageState?.isLoading}
        isFilterDisabled={isFilterBtnDisable()}
      />

      {/* Main Content Area */}
      <Box className="main-content-area">
        <Box className="page-header-container">
          <Box>
            <Typography
              variant="h5"
              className="page-heading"
            >
              Premium BDX
            </Typography>
            {/* DEMO: role context line */}
            <Typography sx={{ fontSize: "12px", color: "#6b7280", marginTop: "4px", fontWeight: 500 }}>
              Analyst view — upload and reconcile bordereaux
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <CommonButton
              text="Add"
              handleClick={() => {
                setTxnMode("ADD");
                setOpenAddFileDialog(true);
              }}
              sx={primaryButtonStyle}
              hoverColor={primaryButtonHoverColor}
              disabled={pageState?.isLoading}
            />
            <IconButton
              onClick={() => setFilterSidebarOpen(!filterSidebarOpen)}
              className="filter-toggle-button"
            >
              <FilterListIcon />
            </IconButton>
          </Box>
        </Box>

        {/* DEMO: guided workflow path */}
        <DemoWorkflowNav
          activeStep={0}
          onStepClick={(step, index) => {
            if (index === 0) {
              setTxnMode("ADD");
              setOpenAddFileDialog(true);
            } else {
              navigate(step.route);
            }
          }}
        />

        {/* DEMO: efficiency badges */}
        {!isDemoSummaryLoading && demoSummary && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5, alignItems: "center" }}>
            <Chip
              icon={<SpeedIcon sx={{ fontSize: "16px !important" }} />}
              label={`${demoSummary.total_records ?? 0} certificates reconciled`}
              size="small"
              sx={{
                fontWeight: 600,
                borderColor: "#1672DE",
                color: "#1672DE",
                backgroundColor: "rgba(22, 114, 222, 0.06)",
              }}
              variant="outlined"
            />
            <Chip
              icon={<CheckCircleIcon sx={{ fontSize: "16px !important", color: "#4caf50 !important" }} />}
              label={`${demoSummary.matched_count ?? 0} matched · ${demoSummary.needs_review_count ?? 0} needs review`}
              size="small"
              sx={{ fontWeight: 600 }}
              variant="outlined"
            />
            {lastUploadStats?.rows_processed != null && (
              <Chip
                label={`Last upload: ${lastUploadStats.rows_processed} rows in ${lastUploadStats.durationSec}s`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500, color: "#6b7280", borderColor: "#d1d5db" }}
              />
            )}
          </Box>
        )}

        {/* DEMO: audit strip — plain-language summary of what the system did */}
        {!isDemoSummaryLoading && demoSummary && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: { xs: 0.5, sm: 1 },
              px: 1.75,
              py: 1,
              mb: 1.5,
              borderRadius: "10px",
              backgroundColor: "#f8fafc",
              border: "1px solid #e8eaf0",
              fontSize: "12.5px",
              color: "#4b5563",
            }}
          >
            {(() => {
              const parsed =
                lastUploadStats?.rows_processed ?? demoSummary.total_records ?? 0;
              const matched =
                lastUploadStats?.matched_count ?? demoSummary.matched_count ?? 0;
              const flagged =
                lastUploadStats?.needs_review_count ?? demoSummary.needs_review_count ?? 0;
              const segments = [
                { label: "Parsed", value: `${parsed} rows`, color: "#1672DE" },
                { label: "Matched", value: matched, color: "#2e7d32" },
                { label: "Flagged", value: flagged, color: "#b26a00" },
              ];
              if (lastUploadStats?.durationSec != null) {
                segments.push({
                  label: "Ready for payout in",
                  value: `${lastUploadStats.durationSec}s`,
                  color: "#4b5563",
                });
              }
              return segments.map((seg, index) => (
                <React.Fragment key={seg.label}>
                  <Box component="span">
                    {seg.label}{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: seg.color }}>
                      {seg.value}
                    </Box>
                  </Box>
                  {index < segments.length - 1 && (
                    <Box component="span" sx={{ color: "#cbd5e1" }}>
                      ·
                    </Box>
                  )}
                </React.Fragment>
              ));
            })()}
          </Box>
        )}

        {/* DEMO: summary cards + match chart */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 220px",
              lg: "repeat(4, 1fr) 220px",
            },
            gap: "12px",
            marginBottom: "12px",
            alignItems: "stretch",
          }}
        >
          {demoSummaryCards.map((card) => {
            const CardIcon = card.Icon;
            return (
              <Card
                key={card.label}
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  padding: "16px 18px",
                  borderRadius: "14px",
                  border: "1px solid #e8eaf0",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                  transition: "transform 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "4px",
                    height: "100%",
                    backgroundColor: card.color,
                  },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.4px",
                      }}
                    >
                      {card.label}
                    </Typography>
                    {isDemoSummaryLoading ? (
                      <Skeleton variant="text" width={80} height={34} sx={{ backgroundColor: "#e0e0e0" }} />
                    ) : (
                      <Typography sx={{ fontSize: "26px", fontWeight: 700, color: card.color, lineHeight: 1.2 }}>
                        {card.value}
                      </Typography>
                    )}
                    {!isDemoSummaryLoading && card.sub && (
                      <Typography sx={{ fontSize: "11px", color: "#9ca3af", mt: 0.25 }}>
                        {card.sub}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: `${card.color}1A`,
                      flexShrink: 0,
                    }}
                  >
                    <CardIcon sx={{ color: card.color, fontSize: "22px" }} />
                  </Box>
                </Box>
              </Card>
            );
          })}
          {/* DEMO: reconciliation breakdown chart */}
          {!isDemoSummaryLoading && demoSummary && matchedTotal > 0 && (
            <Card
              sx={{
                padding: "14px 16px",
                borderRadius: "14px",
                border: "1px solid #e8eaf0",
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                gridColumn: { xs: "1", sm: "1 / span 2", md: "3", lg: "5" },
                gridRow: { xs: "auto", md: "1 / span 2", lg: "auto" },
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.12)",
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: "#6b7280",
                  textTransform: "uppercase",
                  letterSpacing: "0.4px",
                  textAlign: "center",
                  mb: 0.5,
                }}
              >
                Match Breakdown
              </Typography>
              <Box sx={{ position: "relative", height: 110 }}>
                <Doughnut
                  data={matchChartData}
                  options={matchChartOptions}
                  plugins={[matchCenterTextPlugin]}
                />
              </Box>
              {/* DEMO: custom legend with counts */}
              <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: "#4caf50" }} />
                  <Typography sx={{ fontSize: "11px", color: "#4b5563", fontWeight: 600 }}>
                    Matched {matchedCount}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: "#ff9800" }} />
                  <Typography sx={{ fontSize: "11px", color: "#4b5563", fontWeight: 600 }}>
                    Review {needsReview}
                  </Typography>
                </Box>
              </Box>
            </Card>
          )}
        </Box>

        {/* DEMO: first-run explainer when no files have been uploaded yet */}
        {!pageState?.isLoading && (pageState?.data?.length ?? 0) === 0 && (
          <Box
            sx={{
              backgroundColor: "#ffffff",
              border: "1px solid #e8eaf0",
              borderRadius: "14px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
              padding: { xs: "20px", md: "28px 32px" },
              marginBottom: "12px",
            }}
          >
            <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#111827", mb: 0.5 }}>
              How Premium BDX works
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#6b7280", mb: 2.5 }}>
              Upload a bordereau and the system reconciles it against cash allocations end to end.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                gap: 2,
                mb: 3,
              }}
            >
              {[
                { n: 1, title: "Upload BDX", desc: "Bring in the bordereau workbook", Icon: InsertDriveFileOutlinedIcon },
                { n: 2, title: "Auto-match", desc: "Reconcile premiums to cash", Icon: PaidOutlinedIcon },
                { n: 3, title: "Review", desc: "Investigate any variances", Icon: ReportProblemOutlinedIcon },
                { n: 4, title: "Release payment", desc: "Approve matched payouts", Icon: TaskAltIcon },
              ].map((step) => {
                const StepIcon = step.Icon;
                return (
                  <Box
                    key={step.n}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      p: 2,
                      borderRadius: "10px",
                      border: "1px solid #eef0f4",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "9px",
                        backgroundColor: "rgba(22, 114, 222, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <StepIcon sx={{ color: "#1672DE", fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1f2937" }}>
                        {step.n}. {step.title}
                      </Typography>
                      <Typography sx={{ fontSize: "11.5px", color: "#6b7280", mt: 0.25 }}>
                        {step.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
            <CommonButton
              text="Upload first BDX"
              handleClick={() => {
                setTxnMode("ADD");
                setOpenAddFileDialog(true);
              }}
              sx={primaryButtonStyle}
              hoverColor={primaryButtonHoverColor}
              disabled={pageState?.isLoading}
            />
          </Box>
        )}

        <Box
          sx={{
            flex: 1,
          display: "flex",
            flexDirection: "column",
            minHeight: 0,
            position: "relative",
            backgroundColor: "#ffffff",
            borderRadius: "12px",
            boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            overflow: "visible",
            transition: "all 0.3s ease",
            border: "1px solid #e8eaf0",
            paddingBottom: "10px",
            "&:hover": {
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            },
        }}
      >
        <DataGrid
          className={classes.pagination}
          loading={pageState?.isLoading}
          rows={pageState?.data ?? []}
          getRowId={(row) => pageState?.data.indexOf(row)}
          getRowClassName={(params) =>
            pipelineReveal?.filename === params.row?.filename &&
            (!pipelineReveal?.sheetName || pipelineReveal.sheetName === params.row?.sheettab)
              ? "demo-just-uploaded-row"
              : ""
          }
          rowCount={pageState?.total}
          columns={columns}
          keepNonExistentRowsSelected
          pagination
          paginationMode="server"
          disableSelectionOnClick
          page={pageState.page - 1}
          pageSize={pageState?.pageSize ?? 25}
            rowsPerPageOptions={[10, 25, 50, 100]}
          onPageSizeChange={(newPageSize) => {
            return setPageState((old) => {
              setPaginationCount(
                getPaginationCount(pageState?.total, newPageSize),
              );
              return { ...old, pageSize: newPageSize, page: 1 };
            });
          }}
          sx={{
              border: "none",
              flex: 1,
              minHeight: 0,
              "& .MuiDataGrid-root": {
                border: "none",
                height: "100%",
              },
              "& .MuiDataGrid-main": {
                height: "100%",
                "&::-webkit-scrollbar": {
                  width: "8px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f1f1f1",
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#1672DE",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#1565c0",
                  },
                },
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #e0e0e0",
                minHeight: "52px !important",
                maxHeight: "52px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "transparent",
                fontWeight: "600",
                color: "#1672DE",
                fontSize: "14px",
                padding: "12px 16px",
                borderBottom: "none",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "600",
                  fontSize: "14px",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#1672DE",
                },
                "& .MuiDataGrid-menuIcon": {
                  color: "#1672DE",
                },
              },
              // DEMO: highlight the row that was just uploaded
              "& .demo-just-uploaded-row": {
                animation: "demoRowPulse 1.6s ease-in-out infinite",
              },
              "@keyframes demoRowPulse": {
                "0%, 100%": { backgroundColor: "rgba(76, 175, 80, 0.10)" },
                "50%": { backgroundColor: "rgba(76, 175, 80, 0.22)" },
              },
              "& .MuiDataGrid-row": {
                transition: "all 0.2s ease",
                borderBottom: "1px solid #f0f0f0",
                "&:hover": {
                  backgroundColor: "rgba(22, 114, 222, 0.04)",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                },
                "&.Mui-selected": {
                  backgroundColor: "rgba(22, 114, 222, 0.08)",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.12)",
                  },
                },
                "&:nth-of-type(even)": {
                  backgroundColor: "#fafafa",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.04)",
                  },
                },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f0f0f0",
                padding: "12px 16px",
                fontSize: "13px",
                color: "#333",
                display: "flex",
                alignItems: "center",
                "&:focus": {
                  outline: "none",
                },
                "&:focus-within": {
                  outline: "none",
                },
              },
              // DEMO: softer scrollbar
              "& .MuiDataGrid-virtualScroller": {
                overflowX: "auto !important",
                "&::-webkit-scrollbar": {
                  width: "6px",
                  height: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#cbd5e1",
                  borderRadius: "10px",
                  "&:hover": {
                    background: "#94a3b8",
                  },
                },
                scrollbarColor: "#cbd5e1 transparent",
                scrollbarWidth: "thin",
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "2px solid #e0e0e0",
                backgroundColor: "#f8f9fa",
                minHeight: "48px",
                maxHeight: "48px",
                paddingBottom: "8px",
                paddingTop: "8px",
                paddingLeft: "16px",
                paddingRight: "16px",
                position: "relative",
                zIndex: 1,
                "& .MuiTablePagination-root": {
                  color: "#666",
                  paddingRight: "16px",
                  margin: 0,
                  minHeight: "32px",
                },
                "& .MuiTablePagination-selectLabel": {
                  fontSize: "13px",
                  margin: 0,
                },
                "& .MuiTablePagination-displayedRows": {
                  fontSize: "13px",
                  margin: 0,
                },
                "& .MuiTablePagination-actions": {
                  marginLeft: "8px",
                  "& .MuiIconButton-root": {
                    padding: "4px",
                  },
                },
                "& .MuiTablePagination-select": {
                  margin: "0 8px",
                  padding: "4px 8px",
                },
                "& .MuiTablePagination-toolbar": {
                  minHeight: "32px",
                  padding: 0,
                },
              },
              "& .MuiDataGrid-toolbarContainer": {
                padding: "12px 16px",
                backgroundColor: "#f8f9fa",
                borderBottom: "1px solid #e0e0e0",
                "& .MuiButton-root": {
                  color: "#1672DE",
                  fontSize: "13px",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "rgba(22, 114, 222, 0.1)",
                  },
                },
              },
              "& .MuiDataGrid-loadingOverlay": {
                backgroundColor: "rgba(255, 255, 255, 0.8)",
            },
          }}
          components={{
            Toolbar: GridToolbarContainer,
            NoRowsOverlay: () => (
                // DEMO: richer empty state
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{ color: "#999" }}
                >
                  <DownloadIcon sx={{ fontSize: 40, color: "#c7cdd6", transform: "rotate(180deg)" }} />
                  <Typography variant="body1" sx={{ color: "#6b7280", fontWeight: 600 }}>
                    No Premium BDX files yet
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                    Click “Add” to upload a Premium BDX file
                  </Typography>
              </Stack>
            ),
          }}
        />
        <Stack
            sx={{
            position: "absolute",
              bottom: "16px",
              right: "20px",
              zIndex: 10,
              backgroundColor: "#f8f9fa",
              padding: "4px 8px",
              borderRadius: "4px",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <CommonPaginationContainer
            count={paginationCount + 1}
            pageState={pageState}
            setPageState={setPageState}
          />
        </Stack>
        </Box>
      </Box>

      <Dialog
        open={isOpenAddFileDialog}
        disableBackdropClick
        disableEscapeKeyDown
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        PaperProps={{ style: dialogStyle }}
      >
        { isModelProgressBar && <CustomLoader />}
        <DialogTitle id="alert-dialog-title" sx={{ cursor: "default" }}>
          {txnMode === "ADD"
            ? "Add File"
            : txnMode === "EDIT"
              ? "Replace File"
              : null}
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenAddFileDialog(false);
              setFilesData([]);
              setSelectedSheetNames({});
              setOpenViewDialog(false);
              setFileToUpload(null);
              setExcelFileSheetNames(null);
              setSelectedExcelSheetName(null);
              setSelectedExcelSheetJson(null);
            }}
            style={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon className="CloseIconStyle" />
          </IconButton>
          <hr style={{ width: "100%" }} />
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "0px 24px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Grid item xs={12} sx={{ margin: "10px 0px", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
            <Button
              color="primary"
              variant="contained"
              component="label"
              style={{ 
                height: "32px", 
                fontWeight: 600,
                backgroundColor: "#1672DE",
                color: "white",
                fontSize: "12px",
                padding: "6px 12px",
                minWidth: "auto"
              }}
            >
              {txnMode === "ADD"
                ? "Select New File to Upload"
                : txnMode === "EDIT"
                  ? "Select File to Replace"
                  : null}
              <input
                type="file"
                hidden
                name="file"
                accept=".doc,.docx,.xlsx,.csv,.pdf"
                onChange={(e) => {
                  setFilesData([]);
                  setSelectedSheetNames({});
                  handleInputFileChange(e);
                }}
                multiple
                required
              />
            </Button>
            <CommonButton
              text="Save"
              handleClick={() =>
                filesData.length > 0 ? handleFileSave() : null
              }
              disabled={
                filesData.length === 0 || 
                selectedFiles.length === 0 || 
                selectedFiles.some(file => !(selectedSheetNames[file.name] && selectedSheetNames[file.name].length > 0))
              }
              hoverColor="#1565c0"
              style={{
                backgroundColor: (filesData.length === 0 || 
                  selectedFiles.length === 0 || 
                  selectedFiles.some(file => !(selectedSheetNames[file.name] && selectedSheetNames[file.name].length > 0))) ? "#CCCCCC" : "#1672DE",
                color: "white",
                borderRadius: "6px",
                padding: "6px 12px",
                fontWeight: "500",
                fontSize: "12px",
                transition: "all 0.3s ease",
                boxShadow: (filesData.length === 0 || 
                  selectedFiles.length === 0 || 
                  selectedFiles.some(file => !(selectedSheetNames[file.name] && selectedSheetNames[file.name].length > 0))) ? "none" : "0 2px 4px rgba(22, 114, 222, 0.2)",
              }}
            />
          </Grid>
          <Grid item xs={12} sx={{ margin: "10px 0px" }}>
            {filesData.length > 0 && (
              filesData.map((file, index) => (
                <>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: 'space-between',
                    marginBottom: "15px"
                  }}
                  key={index}
                > 
                  <Checkbox
                    checked={selectedFiles.some(f => f.name === file.name)}
                    onChange={(e) => handleCheckboxChange(file, e.target.checked)}
                  />
                  <CustomTooltip title={file.name}>
                    <span style={{ fontSize: "14px", fontWeight: 600, paddingTop: "1.5%" }}>
                      {file.name.length > 70 ? `${file.name.substring(0, 70)}...` : file.name} - Size: {file.size}
                    </span>
                  </CustomTooltip>
                  {fileSheetNamesMap[file.name] && 
                    <Card
                      elevation={5}
                      sx={{ marginTop: "5px", overflow: "auto !important" }}
                    >
                      <div>
                        <FormControl fullWidth style={{ width: '250px' }}>
                          <Select
                            labelId={`select-label-${file.name}`}
                            multiple
                            value={selectedSheetNames[file.name] || []}
                            onChange={(e) => {
                              const value = e.target.value;
                              // Set the selected sheet names to the new selection
                              setSelectedSheetNames(prev => ({
                                ...prev,
                                [file.name]: typeof value === 'string' ? value.split(',') : value,
                              }));
                            }}
                            displayEmpty
                            renderValue={selected => (
                              <Tooltip title={selected.join(', ') || 'No sheets selected'}>
                                <span>{selected.length > 0 ? selected.join(', ') : 'Please select sheet'}</span>
                              </Tooltip>
                            )}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 48 * 4.5 + 8,
                                  width: 250,
                                },
                              },
                            }}
                          >
                            {fileSheetNamesMap[file.name].map((sheetName, index) => (
                              <MenuItem key={index} value={sheetName}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                  <Checkbox checked={(selectedSheetNames[file.name] || []).indexOf(sheetName) > -1} />
                                  <ListItemText primary={sheetName} />
                                  <IconButton
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent the select from closing
                                      handleView(file, sheetName);
                                    }}
                                    size="small"
                                  >
                                    {loadingStates.get(`${file.name}-${sheetName}`) ? <CustomLoader /> : <VisibilityIcon fontSize="small" />}
                                  </IconButton>
                                </div>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </div>
                    </Card>
                  }
                  {/* <Button 
                    style={{ marginTop: "1.5%" }}
                    onClick={() =>
                      filesData.length > 0 ? setHandleIntermediateView(file) : null
                    }
                    disabled={!selectedSheetNames[file.name]}
                  >View</Button> */}
                  {/* <span style={{ ...errorSpanStyle }}>Error</span> */}
                  {/* <Button style={{ marginTop: "1.5%" }} onClick={() => {
                    setSelectedSheetNames(prev => ({
                      ...prev,
                      [file.name]: null
                    }));
                    setFilesData(prevFiles => prevFiles.filter(f => f.name !== file.name));
                  }}>Remove File</Button> */}
                </div>
              </>
              ))
            )}
          </Grid>
          {/* { isApiProgressBar && <CustomLoader />} */}
        </DialogContent>
      </Dialog>
      <Dialog
        open={isOpenViewDialog}
        disableBackdropClick
        disableEscapeKeyDown
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        PaperProps={{ style: dialogStyle }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ cursor: "default", position: "relative" }}
        >
          {"View File"}
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenViewDialog(false);
              if (txnMode === "VIEW") {
                setSelectedExcelSheetJson(null);
              }
            }}
            style={{ position: "absolute", right: 12, top: 12 }}
          >
            <CloseIcon className="CloseIconStyle" />
          </IconButton>
          <hr style={{ width: "100%" }} />
        </DialogTitle>
        <DialogContent
          sx={{
            padding: "30px 30px 30px 30px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%" 
          }}
        >
          <Grid lg={12} display={"flex"} flexDirection={"column"}>
            {isApiProgressBar && <CustomLoader />}
            <Grid
              xs={12}
              lg={12}
              gap={1}
              container
              className="ViewpageContainerItems"
            >
              <GetViewTable
                selectedExcelSheetJson={selectedExcelSheetJson}
                setSelectedExcelSheetJson={setSelectedExcelSheetJson}
                setFilenameArray={setFilenameArray}
                setCertificateRefArray={setCertificateRefArray}
                currentTotalFNP={currentTotalFNP}
                currentSelectedSheetRowCount={currentSelectedSheetRowCount}
                sheetTabName={sheetTabName}
                bdxfileName={bdxfileName}
                commission={commission}
                allFilesResponseData={allFilesResponseData}
              />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
      <Dialog
        open={isOpenWorkflowDialog}
        onClose={() => setIsOpenWorkflowDialog(false)}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "500px",
          },
        }}
      >
        {currentRow.certificateref === "PCY2812924AD" ? (
          <div>
            <DialogTitle id="alert-dialog-title" sx={{ cursor: "default" }}>
              {"Workflow Initiated"}
              <IconButton
                aria-label="close"
                onClick={() => {
                  setIsOpenWorkflowDialog(false);
                }}
                style={{ position: "absolute", right: 12, top: 12 }}
              >
                <CloseIcon className="CloseIconStyle" />
              </IconButton>
              <hr style={{ width: "100%" }} />
            </DialogTitle>
            <DialogContent
              sx={{
                padding: "0px 24px 5px 24px",
                display: "flex",
                fontSize: "12px",
              }}
            >
              <Grid
                lg={12}
                display={"flex"}
                flexDirection={"column"}
                backgroundColor={"rgb(175, 171, 170)"}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                  marginBottom={2}
                >
                  <div>Workflow Name : NO_VARIANCE_WORKFLOW</div>
                  <div>Workflow Status : In Process</div>
                  <div>Current Step : reviewer</div>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  marginBottom={2}
                >
                  <div>Step Name : initiater</div>
                  <div>Initiator Comments :</div>
                  <div>Initiator Name : vijay kumar reddy</div>
                  <div>Date/Time : 2024-06-21 12:40:01.251541</div>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  marginBottom={2}
                >
                  <div>Step Name : reviewer</div>
                  <div>Reviewer Comments :</div>
                  <div>
                    Reviewer Name : Rupesh, lrname, Reviewer0, Shivali123
                  </div>
                  <div>Date/Time :</div>
                  <div>Status :</div>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                  marginBottom={2}
                >
                  <div>Step Name : approver</div>
                  <div>Approver Comments :</div>
                  <div>
                    Approver Name : Test Treasury, maname, sairam, Kerry Fenn,
                    Approver0
                  </div>
                  <div>Date/Time :</div>
                  <div>Status :</div>
                </Grid>
              </Grid>
            </DialogContent>
          </div>
        ) : (
          <div>
            <DialogTitle id="alert-dialog-title" sx={{ cursor: "default" }}>
              {"Workflow Not Initiated"}
              <IconButton
                aria-label="close"
                onClick={() => {
                  setIsOpenWorkflowDialog(false);
                }}
                style={{ position: "absolute", right: 12, top: 12 }}
              >
                <CloseIcon className="CloseIconStyle" />
              </IconButton>
              <hr style={{ width: "100%" }} />
            </DialogTitle>
            <DialogContent
              sx={{
                padding: "0px 24px 5px 24px",
                display: "flex",
                fontSize: "12px",
              }}
            >
              <Grid
                lg={12}
                display={"flex"}
                flexDirection={"column"}
                backgroundColor={"rgb(175, 171, 170)"}
              ></Grid>
              There is variance in data. Please check Exception report or upload
              updated Premium BDX File.
            </DialogContent>
          </div>
        )}
      </Dialog>
      <DialogBox
        title="Delete Confirmation"
        body="Are you sure you want to delete this item? If there are multiple files associated with this file name, all those will be deleted."
        confirmText="Delete"
        isOpen={open}
        onCancel={handleDeleteCancelled}
        onConfirm={handleDeleteConfirmed}
      />
    </div>
  );
};

export default PremiumBDX;
